/**
 * displayDialog
 *
 * Creates and mounts an accessible modal dialog containing the supplied
 * element, then returns a small control object so the caller can close or
 * configure the dialog without holding direct references to the DOM nodes.
 *
 * Accessibility features provided:
 *  - `role="dialog"` + `aria-modal="true"` on the dialog container.
 *  - Focus is moved into the dialog on open and restored on close.
 *  - A focus-trap prevents keyboard focus from escaping the dialog.
 *  - Background content receives `aria-hidden="true"` to suppress it from
 *    screen-reader virtual buffers.
 *  - Pressing Escape closes the dialog and fires any registered close
 *    callbacks.
 *  - A full-screen overlay prevents background scrolling.  Single-touch
 *    scroll events are also blocked on mobile while still allowing
 *    pinch-to-zoom (two-finger gestures pass through).
 */

/**
 * Parse an HTML string and return the first child element.
 *
 * @param {string} str - A valid HTML snippet (e.g. `'<div class="foo">'`).
 * @returns {ChildNode}
 */
function parseHTML(str) {
    const $tmp = document.createElement('div');
    $tmp.innerHTML = str.trim();
    return $tmp.firstChild;
}

/**
 * Selectors for page regions that should be hidden from assistive technology
 * while the dialog is open.
 *
 * @type {string[]}
 */
const BACKGROUND_SELECTORS = [
    '#skiplink-container',
    'body > header',
    '#global-cookie-message',
    'main',
    'body > footer',
    'body > .govuk-skip-link',
    '.cbanner-govuk-cookie-banner',
    'body > .govuk-width-container',
];

/**
 * Pre-joined selector string built once at module load time.
 * Avoids repeatedly calling Array#join inside displayDialog().
 */
const BACKGROUND_SELECTOR_STRING = BACKGROUND_SELECTORS.join(', ');

/**
 * Display a modal dialog containing `$elementToDisplay`.
 *
 * @param {string} htmlToDisplay
 *   The element (or HTML string) to render inside the dialog.
 *
 * @returns {{
 *   closeDialog:       () => void,
 *   setAriaLabelledBy: (value: string|null) => void,
 *   addCloseHandler:   (handler: () => void) => void
 * }}
 */
function displayDialog(htmlToDisplay) {
    /** @type {Array<() => void>} Teardown functions run when the dialog closes. */
    const resetFns = [];

    /** @type {Array<() => void>} Callbacks notified when the dialog is *dismissed* (not just closed). */
    const closeCallbacks = [];

    const $dialog = parseHTML(`
        <div 
            id="timeout-dialog"
            tabindex="-1"
            role="dialog"
            aria-modal="true"
            class="timeout-dialog"
            aria-labelledby="hmrc-timeout-heading hmrc-timeout-message"
        >
        `);

    // Accept either a pre-built Element or a raw HTML string.
    const $content = parseHTML(htmlToDisplay);

    $dialog.appendChild($content);

    const $overlay = parseHTML('<div id="timeout-overlay" class="timeout-overlay">');
    document.body.appendChild($dialog);
    document.body.appendChild($overlay);

    // Prevent the page from scrolling behind the overlay.
    const $html = document.documentElement;
    if (!$html.classList.contains('noScroll')) {
        $html.classList.add('noScroll');
        resetFns.push(() => $html.classList.remove('noScroll'));
    }

    resetFns.push(() => {
        $dialog.remove();
        $overlay.remove();
    });

    // ── Focus management ─────────────────────────────────────────────────────

    /**
     * Move focus into the dialog and install a focus-trap listener so that
     * tabbing outside the dialog re-focuses it.
     */
    const setupFocusTrap = () => {
        function keepFocus(event) {
            // Use the already-captured $dialog reference — avoids a live
            // getElementById lookup on every focus event while the dialog is open.
            if (event.target !== $dialog && !$dialog.contains(event.target)) {
                event.stopPropagation();
                $dialog.focus();
            }
        }

        const $previouslyFocused = document.activeElement;
        $dialog.focus();

        document.addEventListener('focus', keepFocus, true);

        resetFns.push(() => {
            document.removeEventListener('focus', keepFocus, true);
            // Restore focus to whatever was active before the dialog opened.
            if ($previouslyFocused) {
                $previouslyFocused.focus();
            }
        });
    };

    // ── Background aria-hidden ────────────────────────────────────────────────

    // Hide background regions from screen readers for the duration of the dialog.
    document.querySelectorAll(BACKGROUND_SELECTOR_STRING).forEach(($el) => {
        const previousValue = $el.getAttribute('aria-hidden');
        $el.setAttribute('aria-hidden', 'true');

        resetFns.push(() => {
            if (previousValue) {
                $el.setAttribute('aria-hidden', previousValue);
            } else {
                $el.removeAttribute('aria-hidden');
            }
        });
    });

    // ── Close helpers ─────────────────────────────────────────────────────────

    /** Run all teardown functions and unmount the dialog. */
    const close = () => {
        while (resetFns.length > 0) {
            resetFns.shift()();
        }
    };

    /**
     * Close the dialog **and** notify any registered close-callbacks.
     * Used when the user actively dismisses the dialog (e.g. Escape key).
     */
    const closeAndNotify = () => {
        closeCallbacks.forEach((fn) => fn());
        close();
    };

    // ── Keyboard handler ─────────────────────────────────────────────────────

    /** Close the dialog when the user presses Escape. */
    const setupEscapeHandler = () => {
        function keydownListener(e) {
            if (e.key === 'Escape') {
                closeAndNotify();
            }
        }

        document.addEventListener('keydown', keydownListener);
        resetFns.push(() => document.removeEventListener('keydown', keydownListener));
    };

    // ── Mobile scroll prevention ──────────────────────────────────────────────

    /**
     * Block single-touch scroll events so the page cannot be scrolled behind
     * the overlay on mobile devices.  Two-finger gestures (pinch-to-zoom) are
     * intentionally allowed through.
     */
    const preventSingleTouchScroll = () => {
        const handleTouch = (e) => {
            const touches = e.touches || e.changedTouches || [];
            if (touches.length === 1) {
                e.preventDefault();
            }
        };

        document.addEventListener('touchmove', handleTouch, true);
        resetFns.push(() => document.removeEventListener('touchmove', handleTouch, true));
    };

    // ── Wire everything up ────────────────────────────────────────────────────

    setupFocusTrap();
    setupEscapeHandler();
    preventSingleTouchScroll();

    // ── Public API ────────────────────────────────────────────────────────────

    return {
        /** Unmount the dialog without firing close callbacks. */
        closeDialog() {
            close();
        },

        /**
         * Set (or remove) the `aria-labelledby` attribute on the dialog
         * container.  Pass `null` or an empty string to remove the attribute.
         *
         * @param {string|null} value - Space-separated list of element IDs.
         */
        setAriaLabelledBy(value) {
            if (value && $dialog) {
                $dialog.setAttribute('aria-labelledby', value);
            } else {
                $dialog.removeAttribute('aria-labelledby');
            }
        },

        /**
         * Register a callback to be invoked when the dialog is dismissed by
         * the user (e.g. Escape key or "Stay signed in" button).
         *
         * @param {() => void} handler
         */
        addCloseHandler(handler) {
            closeCallbacks.push(handler);
        },
    };
}

export default displayDialog;
